import { useContext, useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Avatar, Box, List, ListItem, styled, CircularProgress } from '@mui/material';

import { DashboardContext } from 'context/DashboardContext';
import { useWallet } from 'context/WalletContext';
import { getXOauthUrl } from 'utils/x';
import statsService from 'services/stats';

import { Text } from 'components/Text';
import Blockie from 'components/Blockie';
import { StandardButton, ConnectButton } from 'components/Button';

const SingleLeaderBoardItem = styled(ListItem)(({ theme, active }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.border.separator}`,
  padding: theme.spacing(2.4, 2),
  height: 82,
  backgroundColor: active ? 'rgba(0, 230, 118, 0.08)' : 'transparent',
}));

const CenteredBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const ScrollableContainer = styled(Box)({
  height: '400px',
  overflowY: 'auto',
  position: 'relative',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
});

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const SkeletonRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2.4, 2),
  height: 82,
  borderBottom: `1px solid ${theme.palette.border.separator}`,
}));

const SkeletonCircle = styled(Box)({
  width: 34,
  height: 34,
  borderRadius: '50%',
  backgroundColor: '#f0f0f0',
  animation: 'pulse 1.5s ease-in-out infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.4 },
    '100%': { opacity: 1 },
  },
});

const SkeletonLine = styled(Box)(({ width = '100px' }) => ({
  width,
  height: 16,
  backgroundColor: '#f0f0f0',
  borderRadius: 4,
  animation: 'pulse 1.5s ease-in-out infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.4 },
    '100%': { opacity: 1 },
  },
}));

const DataRow = memo(({ item, index, startRank = 0, userWallet }) => {
  const { address = '', username = '', avatar = '', points = 0, rank } = item || {};
  const itsMe = address && address === userWallet;

  // Use the rank from API if available, otherwise calculate from position
  const displayRank = useMemo(
    () => (rank ? parseInt(rank) : startRank + index + 1),
    [rank, startRank, index],
  );

  // Format address for display - show "YOU" for current user
  const displayName = useMemo(
    () =>
      itsMe
        ? 'YOU'
        : username || (address ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'Unknown'),
    [itsMe, username, address],
  );

  return (
    <SingleLeaderBoardItem key={address} active={itsMe ? 'active' : ''}>
      <Box display="flex" alignItems="center">
        {avatar ? (
          <Avatar src={avatar} alt={username} sx={{ height: 34, width: 34 }} />
        ) : (
          <Blockie size={4} customAddress={address || '0x0'} />
        )}
        <Box marginLeft={1} maxHeight="40px" display="flex" flexDirection="column" gap={0}>
          <Text fs={16} fw={600} lh="24px" color={itsMe ? 'active' : 'default'}>
            {displayName}
          </Text>
          <Text fs={12} fw={500} lh="16" component="span" align="left">
            {displayRank}
          </Text>
        </Box>
      </Box>
      <Box>
        <Text fs={20} fw={600}>
          {Number(points).toLocaleString()} XP
        </Text>
      </Box>
    </SingleLeaderBoardItem>
  );
});

DataRow.displayName = 'DataRow';

const Leaderboard = () => {
  const { state } = useContext(DashboardContext);
  const { isAuthenticated, twitterUser, wallet } = useWallet();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreAbove, setHasMoreAbove] = useState(true);
  const [userRank, setUserRank] = useState(null); // eslint-disable-line no-unused-vars
  const [initialLoad, setInitialLoad] = useState(true);
  const [currentStartRank, setCurrentStartRank] = useState(0);
  const scrollContainerRef = useRef(null);
  const ITEMS_PER_PAGE = 20;

  const xLink = getXOauthUrl(wallet);
  const userWallet = useMemo(() => localStorage.getItem('linked_main_wallet'), []);

  // Find user's position and get initial data from dashboard context
  const findUserPosition = useCallback(async () => {
    try {
      // Use the dashboard context data first - this includes the user's position
      const { aroundUsers = [], offset = 0 } = state.data?.xLeaderboard ?? {};

      if (aroundUsers.length > 0) {
        const userIndex = aroundUsers.findIndex((user) => user.userAddress === userWallet);

        if (userIndex >= 0) {
          const estimatedRank = offset + userIndex;
          setUserRank(estimatedRank);
          setCurrentStartRank(offset); // Set the starting rank for this data

          // Convert aroundUsers to the expected format and use as initial data
          const formattedAroundUsers = aroundUsers.map((user) => ({
            address: user.userAddress,
            username: user.User?.twitterUser || '',
            avatar: user.User?.avatar || '',
            points: user.totalPoints,
            rank: null, // Will be calculated
          }));

          setLeaderboardData(formattedAroundUsers);
          setInitialLoad(false);

          return estimatedRank;
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding user position:', error);
      return null;
    }
  }, [state.data?.xLeaderboard, userWallet]);

  // Load more data below
  const loadMoreBelow = useCallback(async () => {
    if (loading || !hasMore) {
      return;
    }

    setLoading(true);
    try {
      const response = await statsService.getXLeaderboard({
        skip: currentStartRank + leaderboardData.length,
        limit: ITEMS_PER_PAGE,
      });

      const data = response?.data || [];

      if (Array.isArray(data) && data.length > 0) {
        setLeaderboardData((prev) => [...prev, ...data]);
        setHasMore(data.length === ITEMS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more below:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, currentStartRank, leaderboardData.length]);

  // Load more data above
  const loadMoreAbove = useCallback(async () => {
    if (loading || !hasMoreAbove) {
      return;
    }

    const itemsToLoad = Math.min(ITEMS_PER_PAGE, currentStartRank);
    const newStartRank = Math.max(0, currentStartRank - itemsToLoad);

    if (itemsToLoad === 0) {
      setHasMoreAbove(false);
      return;
    }

    setLoading(true);
    try {
      const response = await statsService.getXLeaderboard({
        skip: newStartRank,
        limit: itemsToLoad,
      });

      const data = response?.data || [];

      if (Array.isArray(data) && data.length > 0) {
        const scrollContainer = scrollContainerRef.current;
        const oldScrollHeight = scrollContainer.scrollHeight;

        setLeaderboardData((prev) => [...data, ...prev]);
        setCurrentStartRank(newStartRank); // Update the starting rank
        setHasMoreAbove(newStartRank > 0);

        // Maintain scroll position
        setTimeout(() => {
          const newScrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop += newScrollHeight - oldScrollHeight;
        }, 0);
      } else {
        setHasMoreAbove(false);
      }
    } catch (error) {
      console.error('Error loading more above:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMoreAbove, currentStartRank]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated && twitterUser && initialLoad && state.data?.xLeaderboard) {
      const initializeLeaderboard = async () => {
        const rank = await findUserPosition();
        if (rank !== null) {
          // User found in dashboard context data, we already set the initial data
          setHasMore(true);
          setHasMoreAbove(rank > 0);
        } else {
          // Fallback: load from the beginning if user not found
          setLoading(true);
          try {
            const response = await statsService.getXLeaderboard({
              skip: 0,
              limit: ITEMS_PER_PAGE,
            });
            const data = response?.data || [];
            setLeaderboardData(data);
            setCurrentStartRank(0);
            setHasMore(data.length === ITEMS_PER_PAGE);
            setHasMoreAbove(false);
          } catch (error) {
            console.error('Error loading fallback leaderboard:', error);
          } finally {
            setLoading(false);
            setInitialLoad(false);
          }
        }
      };

      initializeLeaderboard();
    }
  }, [isAuthenticated, twitterUser, initialLoad, state.data?.xLeaderboard, findUserPosition]);

  // Debounced scroll handler for better performance
  const scrollTimeoutRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loading) {
      return;
    }

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce the scroll handling
    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || loading) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearTop = scrollTop <= 100;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearTop && hasMoreAbove) {
        loadMoreAbove();
      } else if (isNearBottom && hasMore) {
        loadMoreBelow();
      }
    }, 150); // 150ms debounce
  }, [loading, hasMore, hasMoreAbove, loadMoreAbove, loadMoreBelow]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (!isAuthenticated) {
    return (
      <CenteredBox>
        <ConnectButton height={40} />
      </CenteredBox>
    );
  }

  if (!twitterUser) {
    return (
      <CenteredBox>
        <StandardButton href={xLink}>Sign in with 𝕏</StandardButton>
      </CenteredBox>
    );
  }

  // Use the tracked current start rank for correct ranking display

  return (
    <ScrollableContainer ref={scrollContainerRef} onScroll={handleScroll}>
      <List sx={{ marginTop: 0, paddingTop: 0 }}>
        {leaderboardData.map((item, index) => (
          <DataRow
            key={item.address || index}
            item={item}
            index={index}
            startRank={currentStartRank}
            userWallet={userWallet}
          />
        ))}
      </List>
      {loading && initialLoad && (
        // Show skeleton loading for initial load
        <>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i}>
              <Box display="flex" alignItems="center" gap={1}>
                <SkeletonCircle />
                <Box>
                  <SkeletonLine width="80px" />
                  <Box height={4} />
                  <SkeletonLine width="30px" />
                </Box>
              </Box>
              <SkeletonLine width="60px" />
            </SkeletonRow>
          ))}
        </>
      )}
      {loading && !initialLoad && (
        <LoadingContainer>
          <CircularProgress size={24} />
        </LoadingContainer>
      )}
      {!hasMore && !hasMoreAbove && leaderboardData.length > 0 && (
        <Box textAlign="center" p={2}>
          <Text fs={14} color="textSecondary">
            End of leaderboard
          </Text>
        </Box>
      )}
    </ScrollableContainer>
  );
};

export default Leaderboard;
